import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { briefItems } from '@/mocks/brief';
import Reveal from '@/components/base/Reveal';

interface QuickBriefProps {
  query: string;
  onQueryChange: Dispatch<SetStateAction<string>>;
  activeTag: string;
  onSelectTag: (tag: string) => void;
}

const STATUS_TONE: Record<string, string> = {
  已实锤: 'border-primary-300 bg-primary-50 text-primary-700',
  进行中: 'border-accent-300 bg-accent-50 text-accent-800',
  出现反转: 'border-secondary-300 bg-secondary-50 text-secondary-800',
  冷处理: 'border-background-300 bg-background-100 text-foreground-600',
  已澄清: 'border-secondary-200 bg-secondary-50 text-secondary-700',
  已出结果: 'border-foreground-300 bg-background-100 text-foreground-800',
};

const STATUS_SUMMARY = [
  { id: 'ss-1', label: '已实锤', count: 112, tone: 'bg-primary-500' },
  { id: 'ss-2', label: '出现反转', count: 66, tone: 'bg-secondary-500' },
  { id: 'ss-3', label: '进行中', count: 84, tone: 'bg-accent-500' },
  { id: 'ss-4', label: '冷处理', count: 40, tone: 'bg-foreground-400' },
];

export default function QuickBrief({ query, onQueryChange, activeTag, onSelectTag }: QuickBriefProps) {
  const [expanded, setExpanded] = useState(false);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return briefItems.filter((item) => {
      const matchTag = !activeTag || item.tags.includes(activeTag) || item.status === activeTag;
      const matchKeyword =
        !keyword ||
        item.title.toLowerCase().includes(keyword) ||
        item.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchTag && matchKeyword;
    });
  }, [query, activeTag]);

  const visible = expanded ? filtered : filtered.slice(0, 5);

  return (
    <section id="brief" className="w-full bg-background-100 px-4 py-16 md:px-8 md:py-20">
      <div className="mx-auto w-full max-w-7xl">
        <Reveal className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-label text-[11px] tracking-[0.24em] text-primary-600">
              TONIGHT BRIEF / 今夜速览
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              今夜避雷清单，一条一行
            </h2>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="flex h-11 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                type="text"
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
                placeholder="关键词筛选"
                className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 sm:w-44"
              />
              {query && (
                <button type="button" onClick={() => onQueryChange('')} aria-label="清空关键词">
                  <i className="ri-close-circle-fill text-base text-foreground-300" />
                </button>
              )}
            </div>
          </div>
        </Reveal>

        {activeTag && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">当前筛选</span>
            <span className="flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 dark:text-foreground-950">
              {activeTag}
              <button type="button" onClick={() => onSelectTag('')} aria-label="移除筛选">
                <i className="ri-close-line text-sm" />
              </button>
            </span>
            <span className="text-xs text-foreground-400">共 {filtered.length} 条</span>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {visible.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                  <i className="ri-inbox-line text-3xl text-foreground-300" />
                  <p className="text-sm text-foreground-500">
                    没有匹配的条线，换个关键词或清空筛选试试。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      onQueryChange('');
                      onSelectTag('');
                    }}
                    className="rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 dark:text-foreground-950"
                  >
                    重置筛选
                  </button>
                </div>
              )}

              {visible.map((item) => (
                <article
                  key={item.id}
                  className="group flex flex-col gap-3 border-b border-background-100 px-4 py-4 transition-colors last:border-b-0 hover:bg-background-100/70 md:flex-row md:items-center md:gap-5 md:px-5"
                >
                  <div className="flex shrink-0 items-center gap-3 md:w-24 md:flex-col md:items-start md:gap-1">
                    <span className="font-label text-sm font-semibold text-foreground-900">
                      {item.time}
                    </span>
                    <span className="font-label text-[11px] text-foreground-400">{item.date}</span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
                          STATUS_TONE[item.status] ?? STATUS_TONE['进行中']
                        }`}
                      >
                        {item.status}
                      </span>
                      {item.tags.map((tag) => (
                        <span key={tag} className="text-[11px] text-foreground-500">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="mt-2 text-sm font-medium leading-relaxed text-foreground-900 md:text-[15px]">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                      <span>
                        <i className="ri-flag-2-line mr-1 text-foreground-400" />
                        结果：{item.result}
                      </span>
                      <span className="font-label text-foreground-400">
                        <i className="ri-fire-line mr-1 text-primary-500" />
                        {item.heat.toLocaleString()} 围观
                      </span>
                    </p>
                  </div>

                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-background-200 text-foreground-400 transition-colors group-hover:border-primary-300 group-hover:text-primary-600">
                    <i className="ri-arrow-right-line text-base" />
                  </span>
                </article>
              ))}
            </div>

            {filtered.length > 5 && (
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-more-line'} />
                {expanded ? '收起清单' : `展开全部 ${filtered.length} 条`}
              </button>
            )}
          </div>

          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950">避雷状态分布</h3>
                <p className="mt-1 text-xs text-foreground-500">近 7 天条线按最终状态归类</p>

                <div className="mt-5 space-y-4">
                  {STATUS_SUMMARY.map((row) => (
                    <div key={row.id}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-700">{row.label}</span>
                        <span className="font-label text-foreground-500">{row.count}</span>
                      </div>
                      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                        <div
                          className={`h-full rounded-full ${row.tone}`}
                          style={{ width: `${Math.round((row.count / 120) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 dark:text-foreground-950">
                  <i className="ri-alert-line text-lg" />
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-primary-900">看避雷的三条小规矩</h3>
                <ul className="mt-3 space-y-2 text-xs leading-relaxed text-primary-800">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1 text-[6px]" />
                    先看状态标签，再决定要不要读全文
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1 text-[6px]" />
                    "冷处理"不等于"没发生"，条线仍保留
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1 text-[6px]" />
                    有最终结果的条线，优先看结果那一行
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}